import { Grid, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  notaComercialDocumentShortDescriptions,
  notaComercialDocumentLongDescriptions,
} from 'common/genericDescriptions';
import { BooleanValue, Option, laqusUserUploadDocumentsOptions, notaComercialUploadDocumentsOptions } from 'common/genericOptions';
import {
  DocumentInfo,
  TipoDistribuicaoPublica,
  NotaComercialUploadDocumentType,
  DocumentDownloadInfo,
  GrupoAcesso,
  TipoRegistroInstrumentoFinanceiro,
  LaqusUserUploadDocumentType,
} from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, Dropdown, Section, SmSpace } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { RootState } from 'store';
import { updateDocuments, updateDocumentsToDelete } from 'store/modules/notaComercial/uploadDocumentos';
import { v4 as uuid } from 'uuid';

interface DocumentosProps {
  handleGerarTermoConstitutivo(): Promise<DocumentDownloadInfo>;
}

const mapStateToProps = (state: RootState) => ({
  distribuicaoPublica: state.notaComercialCadastro.caracteristicas.distribuicaoPublica,
  tipoDistribuicaoPublica: state.notaComercialCadastro.caracteristicas.tipoDistribuicaoPublica,
  uploadDocumentos: state.notaComercialCadastro.uploadDocumentos,
  typeOfRegister: state.notaComercialCadastro.typeOfRegister,
});

const mapDispatchToProps = { updateDocuments, updateDocumentsToDelete };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & DocumentosProps;

const Documentos: React.FC<Props> = (props) => {
  const {
    uploadDocumentos: { documents, documentsToDelete },
    updateDocuments,
    updateDocumentsToDelete,
    distribuicaoPublica,
    tipoDistribuicaoPublica,
    handleGerarTermoConstitutivo,
    typeOfRegister,
  } = props;

  const isUsuarioCadastroOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes
  );

  const isUsuarioOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes
  );


  const isUsuarioLaqus = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso !== GrupoAcesso.Comum
  );

  const multipleDocTypes = [LaqusUserUploadDocumentType.BoletimSubscricao as string];

  function isDocumentOptional(option: Option): boolean {
    return Object.values(LaqusUserUploadDocumentType).includes(option.value as LaqusUserUploadDocumentType);
  }

  const [uploadDocumentType, setUploadDocumentType] = useState<string>('');

  const [bloqueio, setBloqueio] = useState(!isUsuarioOperacoes);

  const getDocumentOptions = () => {

    const options = [];
    if (isUsuarioLaqus || distribuicaoPublica.value === BooleanValue.Yes) {
      options.push(...laqusUserUploadDocumentsOptions);
    }
    if (distribuicaoPublica.value === BooleanValue.No) {
      const termoSecuritizacaoDoc: Option = {
        value: NotaComercialUploadDocumentType.CartulaEmissao,
        description: notaComercialDocumentShortDescriptions[NotaComercialUploadDocumentType.CartulaEmissao],
      };
      options.push(termoSecuritizacaoDoc);
    }
    if (tipoDistribuicaoPublica.value === TipoDistribuicaoPublica._400) {
      options.push(...notaComercialUploadDocumentsOptions);
    }
    if (tipoDistribuicaoPublica.value === TipoDistribuicaoPublica._476) {
      options.push(...notaComercialUploadDocumentsOptions.filter(
        (option) =>
          option.value !== NotaComercialUploadDocumentType.OficioConcessaoRegistroOfertaPublica &&
          option.value !== NotaComercialUploadDocumentType.ProspectoPreliminarDefinitivo
      ));
    }
    return options;
  };

  const documentOptions = getDocumentOptions();

  useEffect(() => {
    if (!documents.length && documentOptions.length) {
      updateDocuments(
        documentOptions?.map((option) => ({
          id: option.value,
          type: option.value,
          optional: isDocumentOptional(option)
        }))
      );
    }
  }, [documents]);

  useEffect(() => {
    if (documents.length) {
      const newestDocuments = [...documents]
        .sort((doc1, doc2) => {
          const date1 = doc1.remoteFileInfo?.creationDate ? new Date(doc1.remoteFileInfo.creationDate).getTime() : 0;
          const date2 = doc2.remoteFileInfo?.creationDate ? new Date(doc2.remoteFileInfo.creationDate).getTime() : 0;
          return date2 - date1;
        })
        .reduce((acc: DocumentInfo[], doc) => {
          if (!acc.some(d => d.type === doc.type)) {
            acc.push(doc);
          }
          return acc;
        }, []);
      
      const newUploadDocuments = documentOptions.map((option) => {
        const optional = isDocumentOptional(option);
        const document = newestDocuments.find(doc => doc.type === option.value);
        return document ? { ...document, optional } : { type: option.value, optional };
      });
      updateDocuments(newUploadDocuments);
      const newDocumentsToDelete: DocumentInfo[] = [];
      documents.forEach((document) => {
        if (!documentOptions.some((option) => option.value === document.type)) {
          newDocumentsToDelete.push({ ...document });
        }
      });
      updateDocumentsToDelete([...documentsToDelete, ...newDocumentsToDelete]);
    }
  }, []);

  const handleUpdateFile = (attachments: Attachment[]) => {
    let updatedDocuments = [...documents];

    attachments.forEach(attachment => {
      const docMatchingTypeIndex = updatedDocuments.findIndex(doc => attachment.id === doc.type);
      const isMultiple = multipleDocTypes.includes(attachment.id)

      if (isMultiple && updatedDocuments[docMatchingTypeIndex].file) {
        const newDoc = {
          ...updatedDocuments[docMatchingTypeIndex],
          file: attachment.file,
          id: attachment.id + uuid()
        };
        updatedDocuments = [...updatedDocuments, newDoc]
      }
      else {
        updatedDocuments = documents.map((doc, index) => {
          return index == docMatchingTypeIndex ? { ...doc, file: attachment.file } : doc;
        });
      }

      updateDocuments(updatedDocuments)
    })
  };

  const handleDeleteFileType = async (type: string) => {
    for (const document of documents) {
      if (document.type === type && !!document.remoteFileInfo) {
        await updateDocumentsToDelete(documentsToDelete.concat({ ...document }));
      }
    }

    let countOfType = documents.filter(document => document.type === type).length;

    var updatedDocs = documents
      .map((doc) => {
        if (doc.type === type) {
          return { ...doc, file: undefined, remoteFileInfo: undefined }
        }
        return doc;
      })
      .filter((documentInfo) => {
        if (documentInfo.type === type && countOfType !== 1) {
          countOfType--;
          return false;
        }
        return true;
      })

    updateDocuments(updatedDocs);
  };



  const baixarTermoConstitutivo = async () => {
    try {
      const termoConstitutivo = await handleGerarTermoConstitutivo();
      const link = document.createElement('a');
      link.href = termoConstitutivo.url;
      link.target = '_blank';
      link.setAttribute('download', `${termoConstitutivo.fileName}${termoConstitutivo.extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (!isUsuarioOperacoes) {
        setBloqueio(false);
      }
      isDocumentOptional
    } catch (error) { }
  };



  return (
    <Section title={'Upload de Documentos'}>
      {isUsuarioCadastroOperacoes && (
        <Button onClick={baixarTermoConstitutivo} variant="outlined" color="primary" style={{ marginLeft: '12px' }}>
          {'Gerar Termo Constitutivo'}
        </Button>
      )}
      <SmSpace />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Dropdown
            label={'Selecione o tipo do documento'}
            required
            options={documentOptions}
            value={uploadDocumentType ?? ''}
            onChange={setUploadDocumentType}
          //  disabled={!isUsuarioOperacoes && bloqueio}
          />
        </Grid>
        <Grid item xs={12}>
          <UploadDocs
            inputAttachments={documents
              .filter((attachment) => !!attachment.remoteFileInfo)
              .map(({ type, remoteFileInfo }) => ({
                id: type,
                remoteFileInfo,
              }))}
            fileId={`${uploadDocumentType}`}
           // disabled={!isUsuarioOperacoes && bloqueio}
            onChange={handleUpdateFile}
            onDelete={handleDeleteFileType}
            multipleFiles={multipleDocTypes.includes(uploadDocumentType)}
          />
        </Grid>
        <Grid item xs={12}>
          {documents?.filter((doc, index, self) => index === self.findIndex((t) => t.type === doc.type))
            .filter(doc => !(doc.optional && !doc.file && !doc.remoteFileInfo))
            .map((document) => (
              <React.Fragment key={document.id}>
                <Grid container spacing={1}>
                  <Grid item>
                    <CheckCircleIcon color={document.file || document.remoteFileInfo ? 'primary' : 'disabled'} />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2">{notaComercialDocumentLongDescriptions[document.type]}</Typography>
                  </Grid>
                </Grid>
              </React.Fragment>
            ))}
        </Grid>
      </Grid>
    </Section>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Documentos);
